/**
 * `/content/posts/:id` — Post detail with edit, schedule, and publish actions.
 */
import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ImageUploadField } from "@/components/image-upload-field";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  usePost,
  useUpdatePost,
  useDeletePost,
  useSchedulePost,
  usePublishPost,
  useRetryPost,
  useContentAnalytics,
} from "@/hooks/use-content";
import { formatTimestamp } from "@/lib/formatters";

const STATUS_STYLES: Record<string, string> = {
  draft: "bg-gray-100 text-gray-600",
  scheduled: "bg-blue-100 text-blue-700",
  publishing: "bg-yellow-100 text-yellow-700",
  published: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
};

export function ContentPostDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const post = usePost(id);
  const updatePost = useUpdatePost();
  const deletePost = useDeletePost();
  const schedulePost = useSchedulePost();
  const publishPost = usePublishPost();
  const retryPost = useRetryPost();
  const analytics = useContentAnalytics();

  const [editing, setEditing] = useState(false);
  const [draftBody, setDraftBody] = useState("");
  const [draftImageUrl, setDraftImageUrl] = useState("");
  const [publishAt, setPublishAt] = useState("");

  if (post.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-48 w-full rounded-xl" />
      </div>
    );
  }

  if (post.isError || !post.data) {
    return (
      <div className="flex flex-col items-center gap-4 p-12 text-center">
        <p className="text-sm text-destructive">Post not found.</p>
        <Button variant="outline" size="sm" onClick={() => navigate("/content")}>Back to content</Button>
      </div>
    );
  }

  const p = post.data;
  const isDraft = p.status === "draft";
  const canPublish = ["draft", "scheduled", "failed"].includes(p.status);
  const canSchedule = ["draft"].includes(p.status);
  const canRetry = p.status === "failed";
  const canDelete = isDraft;

  const postAnalytics = analytics.data?.items.find((item) => item.post_id === id);

  function startEdit() {
    setDraftBody(p.body);
    setDraftImageUrl(p.media_urls[0] ?? "");
    setEditing(true);
  }

  function saveEdit() {
    if (!id) return;
    const trimmed = draftImageUrl.trim();
    updatePost.mutate(
      {
        id,
        body: {
          body: draftBody,
          media_urls: trimmed ? [trimmed] : [],
        },
      },
      { onSuccess: () => setEditing(false) },
    );
  }

  function handleSchedule(e: React.FormEvent) {
    e.preventDefault();
    if (!id || !publishAt) return;
    schedulePost.mutate({ id, body: { publish_at: new Date(publishAt).toISOString() } });
  }

  function handlePublish() {
    if (!id) return;
    publishPost.mutate(id);
  }

  function handleDelete() {
    if (!id || !canDelete) return;
    if (!window.confirm("Delete this draft post?")) return;
    deletePost.mutate(id, { onSuccess: () => navigate("/content") });
  }

  return (
    <div className="flex flex-col gap-6 max-w-2xl">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <button
            type="button"
            onClick={() => navigate("/content")}
            className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1 hover:underline"
          >
            ← Content
          </button>
          <h1 className="text-xl font-extrabold tracking-tight text-gray-900 capitalize">
            {p.platform} post
          </h1>
          <p className="text-sm text-muted-foreground">{formatTimestamp(p.created_at)}</p>
        </div>
        <span className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${STATUS_STYLES[p.status] ?? ""}`}>
          {p.status}
        </span>
      </header>

      {/* Post body */}
      <Card className="border-gray-200">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-semibold text-gray-700">Post copy</CardTitle>
          {isDraft && !editing && (
            <Button variant="ghost" size="sm" onClick={startEdit}>Edit</Button>
          )}
        </CardHeader>
        <CardContent>
          {editing ? (
            <div className="flex flex-col gap-3">
              <Textarea
                value={draftBody}
                onChange={(e) => setDraftBody(e.target.value)}
                rows={8}
                className="font-mono text-sm"
              />
              <ImageUploadField
                value={draftImageUrl}
                onChange={setDraftImageUrl}
                label="Image"
                required={p.platform === "instagram"}
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={saveEdit} disabled={updatePost.isPending}>
                  {updatePost.isPending ? "Saving…" : "Save"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <p className="text-sm whitespace-pre-wrap text-gray-900">{p.body}</p>
              {p.media_urls[0] && (
                // biome-ignore lint/performance/noImgElement: external CDN previews
                <img
                  src={p.media_urls[0]}
                  alt="Post attachment preview"
                  className="max-h-64 w-auto rounded border border-gray-200 object-contain"
                />
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Metadata */}
      {(p.generation_prompt || p.publish_at || p.published_at || p.platform_post_id) && (
        <Card className="border-gray-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-gray-700">Details</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-2 text-sm">
            {p.generation_prompt && (
              <><span className="text-muted-foreground">Topic</span><span className="col-span-1 truncate">{p.generation_prompt}</span></>
            )}
            {p.publish_at && (
              <><span className="text-muted-foreground">Scheduled</span><span>{formatTimestamp(p.publish_at)}</span></>
            )}
            {p.published_at && (
              <><span className="text-muted-foreground">Published</span><span>{formatTimestamp(p.published_at)}</span></>
            )}
            {p.platform_post_id && (
              <><span className="text-muted-foreground">Platform ID</span><span className="truncate font-mono text-xs">{p.platform_post_id}</span></>
            )}
            {p.generation_cost_micros > 0 && (
              <><span className="text-muted-foreground">AI cost</span><span>${(p.generation_cost_micros / 1_000_000).toFixed(4)}</span></>
            )}
          </CardContent>
        </Card>
      )}

      {/* Post-level stats panel (C4) — shown for published posts */}
      {p.status === "published" && (
        <Card className="border-gray-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-gray-700">Performance</CardTitle>
          </CardHeader>
          <CardContent>
            {analytics.isLoading && (
              <div className="grid grid-cols-3 gap-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                  <Skeleton key={i} className="h-12 rounded-lg" />
                ))}
              </div>
            )}
            {!analytics.isLoading && (
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {postAnalytics?.clicks ?? 0}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">Clicks</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {postAnalytics?.leads ?? 0}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">Leads attributed</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {postAnalytics?.bookings ?? 0}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">Bookings</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        {canSchedule && (
          <form onSubmit={handleSchedule} className="flex items-end gap-2">
            <div className="flex flex-col gap-1">
              <Label htmlFor="publish_at" className="text-xs">Schedule at</Label>
              <Input
                id="publish_at"
                type="datetime-local"
                value={publishAt}
                onChange={(e) => setPublishAt(e.target.value)}
                className="h-8 text-sm"
              />
            </div>
            <Button type="submit" size="sm" variant="outline" disabled={!publishAt || schedulePost.isPending}>
              {schedulePost.isPending ? "Scheduling…" : "Schedule"}
            </Button>
          </form>
        )}

        {canPublish && (
          <Button
            onClick={handlePublish}
            disabled={publishPost.isPending}
            className="self-end"
          >
            {publishPost.isPending ? "Publishing…" : "Publish now"}
          </Button>
        )}

        {canRetry && (
          <Button
            variant="outline"
            onClick={() => id && retryPost.mutate(id)}
            disabled={retryPost.isPending}
            className="self-end"
          >
            {retryPost.isPending ? "Retrying…" : "Retry publish"}
          </Button>
        )}

        {canDelete && (
          <Button
            variant="destructive"
            size="sm"
            onClick={handleDelete}
            disabled={deletePost.isPending}
            className="self-end ml-auto"
          >
            Delete draft
          </Button>
        )}
      </div>

      {(publishPost.isError || schedulePost.isError || retryPost.isError) && (
        <p className="text-sm text-destructive">
          {(publishPost.error ?? schedulePost.error ?? retryPost.error) instanceof Error
            ? (publishPost.error ?? schedulePost.error ?? retryPost.error)?.message
            : "Action failed. Please try again."}
        </p>
      )}
    </div>
  );
}

export default ContentPostDetailPage;
